/** AnalyticsTab — InsightIQ / Phyllo analytics for a roster entry. */
import React, { useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { ChartContainer } from '../../../components/ui/chart';
import { useCreatorAnalyticsMetrics, useCampaignCapabilitiesStore, useCampaignUiStore } from '@discovr/features';
import { resolvePipeline } from '../../utils/campaignPipeline';
import type { RosterRowRef } from '@discovr/features';
import { Zap, TrendingUp, Eye, ThumbsUp, MessageSquare, ChevronDown, Search } from '../../../icons';
import { OverviewKpiCard, type OverviewKpiCardProps } from './charts/OverviewKpiCard';
import { useCampaignPanelControls } from '../../useCampaignPanelControls';
import { InfoTip } from './charts/InfoTip';
import type { InsightIQProfile, InsightIQAudience, InsightIQAudienceLikers, InsightIQCreatorDoc } from '@discovr/types';
import { fmtNum, fmtDecimal2 } from './utils/formatters';
import {
  buildAgeGender, buildCountries, buildFollowerTypes,
  buildInterests, buildCities, buildStates, buildFollowerReachability,
  themesForChart,
} from './utils/dataBuilders';
import { Donut } from './charts/Donut';
import { RankBarRow } from './charts/RankBarRow';
import { CountryFlag } from '../../../components/common/CountryFlag';
import { StatCard, Badge, SectionTitle } from './charts/StatCard';
import { DetailedBarChart } from './charts/DetailedBarChart';
import { StackedBarChart } from './charts/StackedBarChart';
import { FollowerReachabilityChart } from './charts/FollowerReachabilityChart';
import { InterestChips } from './charts/InterestChips';
import { ContentTabsSection } from './sections/ContentTabsSection';
import { ReputationHistorySection } from './sections/ReputationHistorySection';
import { EngagementHistogramSection } from './sections/EngagementHistogramSection';
import { LikerCredibilitySection } from './sections/LikerCredibilitySection';
import { CreatorGmvSection } from './sections/CreatorGmvSection';
import { PricingSection } from './sections/PricingSection';
import { PriceExplanationsSection } from './sections/PriceExplanationsSection';
import { MiniProfileCarousel } from './sections/MiniProfileCarousel';
import { ProfileRelevantTagsSection } from './sections/ProfileRelevantTagsSection';
import { CollapsibleSection } from './sections/CollapsibleSection';
import { AudienceCommentersTab } from './sections/AudienceCommentersTab';

// ─── Colour palette (Recharts only — all other colour via Tailwind) ───────────
// BLUE is the Figma audience-bar brand purple (interests/themes). Top
// Demographic + Top Languages use their own two-tone palettes via RankBarRow.
const BLUE = '#6C09F7';

// Audience toggle-pill display copy (Figma node 1741:…): the raw audience
// source keys map to friendlier labels — "Subscribers" (not "Followers") and
// "Comments" (not "Commenters"); likers stays "Likers".
const AUDIENCE_TAB_LABEL: Record<'followers' | 'likers' | 'commenters', string> = {
  followers: 'Subscribers',
  likers: 'Likers',
  commenters: 'Comments',
};

// Map a 2-letter language code to a display name (e.g. "en" → "English"),
// mirroring how countries map their code to a full name. Uses the browser's
// Intl.DisplayNames where available; falls back to the upper-cased code so an
// unknown/unsupported code still renders rather than breaking.
function languageDisplayName(code: string): string {
  const trimmed = code.trim();
  if (!trimmed) return '—';
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'language' });
    const name = dn.of(trimmed.toLowerCase());
    if (name && name.toLowerCase() !== trimmed.toLowerCase()) return name;
  } catch {
    // Intl.DisplayNames unsupported — fall through to the code.
  }
  return trimmed.toUpperCase();
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function AnalyticsSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-4 h-4 w-32 rounded-sm bg-muted" />
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map((j) => (
              <div key={j} className="h-14 rounded-xl bg-muted" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="mb-3 text-heading-h4">📊</div>
      <p className="text-body-sm-semibold text-foreground">No analytics data yet</p>
      <p className="mt-1 text-caption-regular text-muted-foreground">
        Analytics are fetched automatically when this tab is first opened.
      </p>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface AnalyticsTabProps {
  entryId: string;
  creator: RosterRowRef;
}

export function AnalyticsTab({ entryId, creator }: AnalyticsTabProps) {
  const { data: envelope, isLoading, error } = useCreatorAnalyticsMetrics(entryId);

  if (isLoading) return <AnalyticsSkeleton />;

  // Show skeleton for any non-done state — pending/processing/retrying/failed
  // all poll every 5 s until the job completes on the BE side.
  if (!envelope || envelope.status !== 'done' || !envelope.data) {
    return error ? <EmptyState /> : <AnalyticsSkeleton />;
  }

  return <AnalyticsContent data={envelope.data ?? {}} creator={creator} />;
}

// ─── Top Mentions / Top Hashtags pill section (Figma 1834:63647 / 1834:63680)
// Figma gives mention pills (@) a purple tone and hashtag pills (#) a teal
// tone — distinct from the generic gray pill used elsewhere in this panel.
const PILL_TONE: Record<'@' | '#', { bg: string; text: string }> = {
  '@': { bg: '#EDEAFC', text: '#4F39C7' },
  '#': { bg: '#F0FDF9', text: '#0B7A57' },
};

function PillMoreSection({
  title,
  items,
  prefix,
}: {
  title: string;
  items?: Array<{ name: string }> | null;
  prefix: '@' | '#';
}) {
  const [expanded, setExpanded] = useState(false);
  if (!items || items.length === 0) return null;
  const LIMIT = 8;
  const shown = expanded ? items : items.slice(0, LIMIT);
  const hasMore = items.length > LIMIT;
  const fmt = (n: string) => `${prefix}${String(n).replace(/^[@#]/, '')}`;
  const tone = PILL_TONE[prefix];
  return (
    <div className="mb-4 rounded-2xl border border-border bg-card p-5">
      <div className="mb-4 text-heading-h6 text-[#171717]">{title}</div>
      <div className="flex flex-wrap items-center gap-2">
        {shown.map((it, i) => (
          <span
            key={`${it.name}-${i}`}
            className="rounded-full px-3 py-1 text-body-sm-medium"
            style={{ background: tone.bg, color: tone.text }}
          >
            {fmt(it.name)}
          </span>
        ))}
        {hasMore && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 text-body-sm-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {expanded ? 'Less' : 'More'}
            <ChevronDown size={14} className={expanded ? 'rotate-180' : ''} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Content Types donut (Figma 2019-68968) ─────────────────────────────────
const CONTENT_TYPE_COLORS = ['#6C09F7', '#DA58FE', '#FDBA74', '#A78BFA', '#F472B6', '#38BDF8'];

function fmtContentType(raw?: string): string {
  const t = String(raw ?? 'Other').replace(/_/g, ' ').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : 'Other';
}

function ContentTypesCard({ profile }: { profile: InsightIQProfile }) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const stats = (profile.recent_posts_stat ?? []).filter((s) => (s.value ?? 0) > 0);
  if (stats.length === 0) return null;
  const totalVal = stats.reduce((s, x) => s + (x.value ?? 0), 0) || 1;
  const total = profile.content_count ?? totalVal;
  const rows = stats.map((s, i) => ({
    label: fmtContentType(s.content_type ?? s.type),
    value: s.value ?? 0,
    pct: Math.round(((s.value ?? 0) / totalVal) * 100),
    color: CONTENT_TYPE_COLORS[i % CONTENT_TYPE_COLORS.length] ?? '#6C09F7',
  }));
  const segments = rows.map((r) => ({ pct: (r.value / totalVal) * 100, color: r.color }));
  const hovered = hoveredIdx != null ? rows[hoveredIdx] : null;

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="text-heading-h6 text-[#171717]">Content Types</div>
      <div className="mb-4 text-body-sm-regular text-[#737373]">Breakdown of content formats</div>
      <div className="flex items-center gap-5">
        <div className="relative shrink-0" style={{ width: 108, height: 108 }}>
          <Donut segments={segments} activeIndex={hoveredIdx} onHoverChange={setHoveredIdx} />
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-heading-h6 leading-none text-[#171717]">
              {hovered ? `${hovered.pct}%` : fmtNum(total)}
            </span>
            <span className="mt-0.5 text-caption-regular text-[#737373]">
              {hovered ? hovered.label : 'POSTS'}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {rows.map((r, i) => (
            <div
              key={r.label}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              className={`-mx-1 flex items-center gap-2 rounded-md px-1 py-0.5 text-caption-regular transition-colors ${
                hoveredIdx === i ? 'bg-muted' : ''
              }`}
            >
              <span className="size-2 shrink-0 rounded-full" style={{ background: r.color }} />
              <span className="font-semibold text-foreground">{r.label}</span>
              <span className="text-muted-foreground">
                {fmtNum(r.value)} · {r.pct}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Analytics content (renders when data is ready) ──────────────────────────

interface AnalyticsContentProps {
  data: Record<string, unknown>;
  creator: RosterRowRef;
}

function AnalyticsContent({ data, creator: _creator }: AnalyticsContentProps) {
  // Role determines display formatting for the Phyllo metrics that BE also
  // strips from the brand response. Admin keeps Phyllo's raw values (full
  // integer for views, raw decimal for engagement rate — no K/M suffix,
  // no .toFixed cap) so the team can spot-check the brand's uploaded sheet
  // against Phyllo's ground truth. Brand never reaches this code path
  // because the underlying fields arrive as null and the existing
  // `!= null` guards skip the cards entirely.
  const lane = useCampaignCapabilitiesStore((s) => s.lane);
  const isAdmin = lane === 'admin';

  // Brand-safety CTA wiring: the banner button switches the drawer to the
  // Brand Safety tab (mirrors how CreatorInsightsDrawer's onTabChange persists
  // the tab to both Zustand and the URL).
  const { setInsightsTab } = useCampaignPanelControls();

  // Platform comes from our own domain (the campaign), not from the analytics
  // vendor's payload. Campaigns are single-platform; the campaign's platform
  // field is the documented driver of Follower/Subscriber labeling (see
  // roster.ts). This avoids depending on vendor data shape AND fixes the brand
  // view, where follower_count is stripped (so the old count-inference
  // mislabeled Instagram creators as "Subscriber"). Unknown/unresolved → treat
  // as Instagram (the common default) until the header query settles.
  const campaignPlatform = useCampaignUiStore((s) => s.campaignPlatform);
  const brandSafetyEnabled = resolvePipeline(useCampaignUiStore((s) => s.campaignPipeline)).brand_safety_enabled;
  const isYouTube = (campaignPlatform ?? '').toLowerCase().includes('youtube');
  const isInstagram = !isYouTube;
  const audienceNoun = isYouTube ? 'Subscriber' : 'Follower';
  // Subscriber/Follower Quality section hidden per request (YT + IG).
  const SHOW_FOLLOWER_QUALITY = false;

  // The API returns a Phyllo creator doc: { id, work_platform, profile,
  // creator_gmv, fetched_at, ... }. Earlier code collapsed root and profile
  // into one variable, making sibling-of-profile fields (creator_gmv,
  // pricing, etc.) unreachable. Keep them distinct: `doc` for root-level
  // access, `iqProfile` for profile-nested access.
  const doc = (data ?? {}) as InsightIQCreatorDoc;
  const iqProfile = (doc.profile ?? {}) as InsightIQProfile;
  const iqAudience = (iqProfile.audience ?? {}) as InsightIQAudience;
  const iqLikers = (iqProfile.audience_likers ?? {}) as InsightIQAudienceLikers;
  const overview = (data.overview ?? null) as Record<string, unknown> | null;
  const detailed = (data.detailed ?? null) as Record<string, unknown> | null;

  // ── Tab state — must be before derived data so activeAudience is stable ────
  // Per-tab data-presence checks. Each tab is shown only when its source
  // has something to render; the tab switcher row appears only when 2+
  // sources have data; the Audience block as a whole hides when zero.
  const hasFollowerData = !!(
    (iqAudience.countries?.length ?? 0) > 0 ||
    (iqAudience.gender_age_distribution?.length ?? 0) > 0 ||
    iqAudience.credibility_score != null
  );
  const hasLikerData = !!(iqLikers.countries || iqLikers.gender_age_distribution);
  const hasCommenterData = !!(
    iqProfile.audience_commenters &&
    (iqProfile.audience_commenters.credibility_score != null ||
      (iqProfile.audience_commenters.countries?.length ?? 0) > 0 ||
      (iqProfile.audience_commenters.gender_age_distribution?.length ?? 0) > 0 ||
      (iqProfile.audience_commenters.significant_commenters?.length ?? 0) > 0)
  );
  // Default to the first source that has data — followers > likers >
  // commenters — so a creator with only liker/commenter data opens on a
  // populated tab, not an empty Followers panel.
  const [audienceTab, setAudienceTab] = useState<'followers' | 'likers' | 'commenters'>(() => {
    if (hasFollowerData) return 'followers';
    if (hasLikerData) return 'likers';
    if (hasCommenterData) return 'commenters';
    return 'followers';
  });
  const [demoTab, setDemoTab] = useState<'country' | 'state' | 'city'>('country');
  // Hover index for the two Follower/Subscriber Quality donuts (Row 1's big
  // one and the smaller one lower in the page) — separate state per instance
  // since both can independently show a different followerTypes breakdown.
  const [qualityHoveredIdx, setQualityHoveredIdx] = useState<number | null>(null);
  const [qualityHoveredIdx2, setQualityHoveredIdx2] = useState<number | null>(null);
  // If the selected tab's data disappears on a re-render (different creator,
  // SSE refresh, etc) snap to the first available tab so the panel never
  // renders an empty block. Adjust-during-render instead of an effect: the
  // guard is self-correcting (once audienceTab is valid the branch stops), so
  // it settles in one extra render with no cascading loop.
  const validAudienceTabs: Array<'followers' | 'likers' | 'commenters'> = [];
  if (hasFollowerData) validAudienceTabs.push('followers');
  if (hasLikerData) validAudienceTabs.push('likers');
  if (hasCommenterData) validAudienceTabs.push('commenters');
  if (!validAudienceTabs.includes(audienceTab)) {
    setAudienceTab(validAudienceTabs[0] ?? 'followers');
  }
  // Convenience for render-time checks below (cheap to recompute).
  const audienceTabCount =
    (hasFollowerData ? 1 : 0) + (hasLikerData ? 1 : 0) + (hasCommenterData ? 1 : 0);
  // All audience sections (countries, age/gender, interests, follower quality)
  // switch together when the user selects Likers. Fields absent on iqLikers
  // (cities, follower_types, interests, credibility) return null from their
  // builders and those sections simply don't render.
  const activeAudience = (audienceTab === 'likers' && hasLikerData ? iqLikers : iqAudience) as InsightIQAudience;

  // ── Audience data ───────────────────────────────────────────────────────────
  const ageGenderData = buildAgeGender((activeAudience.gender_age_distribution as never[]) ?? []) ?? [];
  const countryData = buildCountries((activeAudience.countries as never[]) ?? []);
  const cityData = buildCities((activeAudience.cities as never[]) ?? []);
  const stateData = buildStates((activeAudience.states as never[]) ?? []);
  // Figma "Top Demographic" (node 2019:69314): Country / State / City share one
  // card with a segmented toggle. Only surface dimensions that have data.
  const demoDims = [
    countryData?.length ? { key: 'country' as const, label: 'Country', rows: countryData } : null,
    stateData?.length ? { key: 'state' as const, label: 'State', rows: stateData } : null,
    cityData?.length ? { key: 'city' as const, label: 'City', rows: cityData } : null,
  ].filter(Boolean) as {
    key: 'country' | 'state' | 'city';
    label: string;
    rows: { label: string; val: number; code?: string }[];
  }[];
  const activeDemo = demoDims.find((d) => d.key === demoTab) ?? demoDims[0];
  const reachabilityData = buildFollowerReachability((iqAudience.follower_reachability as never[]) ?? []);
  const followerTypes = buildFollowerTypes((activeAudience.follower_types as never[]) ?? []);
  // credibility_score from Phyllo is a 0–1 fraction; multiply to get 0–100.
  const credibility = typeof activeAudience.credibility_score === 'number'
    ? Math.round(activeAudience.credibility_score * 100)
    : null;
  // % of the audience that are "significant" (notable/influential) accounts.
  // Phyllo returns this for YouTube too (where follower_types/credibility are
  // empty), so it's the one subscriber-quality signal we can show for YT.
  const significantPct = typeof activeAudience.significant_followers_percentage === 'number'
    ? activeAudience.significant_followers_percentage
    : null;
  const interests = buildInterests((activeAudience.interests as never[]) ?? []);
  // Prefer the audience's brand affinity; fall back to the creator's own when
  // the audience list is EMPTY or absent (Phyllo returns [] for YouTube and some
  // creators — not undefined, so a plain `??` wouldn't fall back). null when
  // neither has any, so the section stays hidden instead of rendering an
  // empty-titled block (matches the buildX helpers, which return null on empty).
  const brandAffinitySrc = (iqAudience.brand_affinity?.length
    ? iqAudience.brand_affinity
    : iqProfile.brand_affinity) as { name: string }[] | undefined;
  const brandAffinity = brandAffinitySrc?.length
    ? brandAffinitySrc.slice(0, 12).map((b) => b.name)
    : null;

  // ── Likers gender fallback (gender_distribution when gender_age_distribution absent) ─
  const lkGenderSimple = (() => {
    const dist = audienceTab === 'likers' ? (iqLikers.gender_distribution ?? []) : [];
    if (!dist.length) return null;
    let male = 0; let female = 0;
    for (const g of dist) {
      if (String(g.gender).toUpperCase() === 'MALE') male += g.value;
      else female += g.value;
    }
    const total = male + female || 1;
    return { malePct: Math.round((male / total) * 100), femalePct: Math.round((female / total) * 100) };
  })();

  // ── Channel stats — use != null to cover both null (Go omitempty) and undefined ──
  const iqEng = iqProfile.engagement_rate != null ? iqProfile.engagement_rate : null;
  const iqViews = iqProfile.average_views != null ? iqProfile.average_views : null;
  const iqAvgLikes = iqProfile.average_likes != null ? iqProfile.average_likes : null;
  const iqAvgComments = iqProfile.average_comments != null ? iqProfile.average_comments : null;

  const engRatePct = iqEng;
  const avgViews = iqViews;

  // ── Reputation growth history ────────────────────────────────────────────────
  const reputationHistory = (iqProfile.reputation_history ?? [])
    .map((h, idx, arr) => {
      const raw = Number(h.subscriber_count ?? 0);
      const prev = idx > 0 ? Number(arr[idx - 1]?.subscriber_count ?? 0) : null;
      const growthPct = prev && prev > 0 ? ((raw - prev) / prev) * 100 : null;
      return { month: String(h.month ?? '').slice(0, 7), subscribersRaw: raw, growthPct };
    });

  const themeRows = themesForChart(detailed ?? undefined);
  // Only show growth chart if at least one data point has a real subscriber count.
  const hasGrowthData = reputationHistory.some((r) => r.subscribersRaw > 0);

  // Y-axis floor for the growth chart, padded to sit just under the data's
  // real range instead of anchoring at 0 — a slow, steady trend (e.g.
  // 195K→260K) reads as a flat line otherwise. Padding is 15% of the
  // observed range (min 2% of the max, so a near-flat series still gets a
  // visible buffer), floored at 0 since subscriber counts are never negative.
  const growthValues = reputationHistory.map((r) => r.subscribersRaw).filter((v) => v > 0);
  const growthMin = growthValues.length ? Math.min(...growthValues) : 0;
  const growthMax = growthValues.length ? Math.max(...growthValues) : 0;
  const growthPad = Math.max((growthMax - growthMin) * 0.15, growthMax * 0.02, 1);
  const growthDomainMin = Math.max(0, Math.floor(growthMin - growthPad));

  // ── Follower type donut ──────────────────────────────────────────────────────
  // buildFollowerTypes already resolves each type's color from the raw Phyllo
  // key; use it directly. (Re-deriving from t.label broke for "Mass
  // Subscribers"/"Suspicious Account" whose labels don't reverse-map to the
  // meta keys, collapsing those slices to the same fallback gray.)
  const donutSegments = followerTypes
    ? followerTypes.map((t) => ({ pct: t.pctNum, color: t.color }))
    : [];

  // ── Gender summary helper ────────────────────────────────────────────────────
  const genderSummary = (dist: { gender: string; age_range: string; value: number }[] | null) => {
    if (!dist?.length) return null;
    let male = 0; let female = 0;
    for (const d of dist) { if (d.gender?.toUpperCase() === 'MALE') male += d.value; else female += d.value; }
    const total = male + female || 1;
    return { malePct: Math.round((male / total) * 100), femalePct: Math.round((female / total) * 100) };
  };

  const followerGender = genderSummary((activeAudience.gender_age_distribution as never) ?? null) ?? lkGenderSimple;

  // suppress unused overview warning — kept for future use
  void overview;

  // ── Overview KPI cards (Figma node 1725:31430) ───────────────────────────────
  // Each card renders only when its source value is non-null (graceful).
  // NOTE: no period-over-period deltas — the only historical series wired in
  // this file is subscriber_count growth (reputationHistory); there is no real
  // delta for engagement/views/likes/comments, so deltas are intentionally
  // omitted rather than fabricated.
  // engagement_rate arrives as a 0–1 fraction from Phyllo (e.g. 0.0494), so
  // multiply by 100 for the displayed percent — matching EngagementHistogramSection.
  // Admin keeps the raw fraction string for ground-truth spot-checks.
  // Chevron → Reputation History: only KPIs with a matching history series
  // (≥2 points) get a chevron that scrolls to that graph. The id matches the
  // slug ReputationHistorySection assigns each MetricCard. Engagement Rate has
  // no reputation series, so it never gets a chevron.
  const repRows = (iqProfile.reputation_history ?? []) as Array<Record<string, unknown>>;
  const repAnchor = (kpiLabel: string, field: string): string | undefined =>
    repRows.filter((r) => r[field] != null).length >= 2
      ? `rep-${kpiLabel.toLowerCase().replace(/\s+/g, '-')}`
      : undefined;

  const engRateDisplay = engRatePct != null ? fmtDecimal2(engRatePct * 100) : null;

  const overviewKpis = [
    engRateDisplay != null && {
      icon: TrendingUp,
      label: 'Engagement Rate',
      value: isAdmin ? String(engRatePct) : `${engRateDisplay}%`,
    },
    avgViews != null && {
      icon: Eye, label: 'Avg Views', value: fmtNum(avgViews),
      repAnchorId: repAnchor('Avg Views', 'average_views'),
    },
    iqAvgLikes != null && {
      icon: ThumbsUp, label: 'Avg Likes', value: fmtNum(iqAvgLikes),
      repAnchorId: repAnchor('Avg Likes', 'average_likes'),
    },
    iqAvgComments != null && {
      icon: MessageSquare, label: 'Avg Comments', value: fmtNum(iqAvgComments),
      repAnchorId: repAnchor('Avg Comments', 'average_comments'),
    },
  ].filter(Boolean) as OverviewKpiCardProps[];

  return (
    <div>
      {/* ── Run safety analysis banner (Figma node 1725:31430) ───────────── */}
      {brandSafetyEnabled && (
      <div
        className="relative mb-4 flex items-center justify-between gap-4 overflow-hidden rounded-2xl px-6 py-5"
        style={{
          background: 'linear-gradient(90deg, #F7F5FE 0%, #F0ECFB 100%)',
          border: '1px solid #EADFFB',
          borderRadius: 16,
        }}
      >
        {/* Figma lightning-bolt texture — tiled bolts on the right, faded in */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 left-1/3"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='30' height='30' viewBox='0 0 30 30'%3E%3Cpath d='M17 4l-6 10h4l-2 8 7-11h-4z' fill='%23C4B5FD'/%3E%3C/svg%3E\")",
            backgroundSize: '26px 26px',
            opacity: 0.7,
            WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 55%)',
            maskImage: 'linear-gradient(90deg, transparent, #000 55%)',
          }}
        />
        <div className="relative">
          <div className="text-heading-h7 text-[#171717]">Is this creator brand safe?</div>
          <div className="flex items-center gap-1.5 text-body-sm-regular text-[#737373]">
            <Search size={14} />
            Our AI safety agent checks if this creator fits your brand
          </div>
        </div>
        <button
          type="button"
          onClick={() => setInsightsTab('brand-safety')}
          className="relative inline-flex items-center gap-2"
          style={{
            background: '#6C09F7',
            color: '#fff',
            border: 'none',
            borderRadius: 999,
            height: 44,
            padding: '0 22px',
            fontSize: 15,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <Zap size={18} />
          Run safety analysis
        </button>
      </div>
      )}

      {/* ── Content Types + Overview KPIs (Figma 2019-68968) ─────────────── */}
      {overviewKpis.length > 0 &&
        (() => {
          const hasContentTypes = (iqProfile.recent_posts_stat ?? []).some((s) => (s.value ?? 0) > 0);
          return (
            <div className={`mb-4 grid grid-cols-1 gap-4 ${hasContentTypes ? 'lg:grid-cols-[2fr_3fr]' : ''}`}>
              {hasContentTypes && <ContentTypesCard profile={iqProfile} />}
              <div className="rounded-2xl border border-border bg-card p-5">
                <div className="mb-4">
                  <div className="text-heading-h6 text-[#171717]">Overview</div>
                  <div className="text-body-sm-regular text-[#737373]">
                    Complete Creator Performance Snapshot
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
                  {overviewKpis.map((kpi) => (
                    <OverviewKpiCard key={kpi.label} {...kpi} />
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

      {/* Top Mentions + Top Hashtags render lower down (Figma order: after the
          Engagement histogram, before Content). */}

      {/* ── Audience section ─────────────────────────────────────────────── */}
      {/* Hide the whole block when no source has data — avoids an empty
          card with just a section title and nothing inside. */}
      {audienceTabCount > 0 && (
      <div className="rounded-2xl border border-border bg-card p-5 mb-4">
        <div className="mb-4 flex items-start justify-between gap-4">
          {/* Title + subtitle treatment matches Overview (Figma node 1741:…) */}
          <div>
            <div className="text-heading-h6 text-[#171717]">Audience Demographics</div>
            <div className="text-body-sm-regular text-[#737373]">
              Deep-Dive Into Creator Metrics
            </div>
          </div>
          {/* Tab switcher only when 2+ sources have data; one-source case
              renders the content with no switcher needed. Labels map raw keys
              to Figma copy: followers→Subscribers, commenters→Comments. */}
          {audienceTabCount > 1 && (
            <div className="flex rounded-full bg-muted p-0.5 text-caption-medium">
              {([
                ...(hasFollowerData ? ['followers' as const] : []),
                ...(hasLikerData ? ['likers' as const] : []),
                ...(hasCommenterData ? ['commenters' as const] : []),
              ] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setAudienceTab(t)}
                  className={`rounded-full px-3 py-1 transition-colors ${audienceTab === t ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  {AUDIENCE_TAB_LABEL[t]}
                </button>
              ))}
            </div>
          )}
        </div>

        {audienceTab !== 'commenters' && (<>
        {/* Row 1 — AUDIENCE FIT + {noun} QUALITY donut (Figma 2019-68968).
            The Quality donut only exists where follower-quality data is present
            (Instagram). Without it (e.g. YouTube) Audience Fit spans full width
            instead of sitting half-width beside an empty column. */}
        {(ageGenderData.length > 0 || (followerTypes?.length ?? 0) > 0) && (() => {
          const hasQualityDonut = !!(followerTypes && followerTypes.length > 0 && donutSegments.length > 0);
          return (
          <div className={`grid grid-cols-1 gap-6 ${hasQualityDonut ? 'lg:grid-cols-2' : ''}`}>
            {ageGenderData.length > 0 && (
              <div className="rounded-xl border border-border p-6">
                <div className="mb-4 text-body-sm-semibold uppercase text-[#171717]">Audience Fit</div>
                <StackedBarChart data={ageGenderData} />
                {followerGender && (
                  <div className="mt-4 flex justify-center gap-6 text-body-sm-medium text-[#404040]">
                    <span className="inline-flex items-center gap-2">
                      <span className="size-[11px] rounded-full bg-[#9647f8]" /> {followerGender.malePct}% Male
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <span className="size-[11px] rounded-full bg-[#e0508a]" /> {followerGender.femalePct}% Female
                    </span>
                  </div>
                )}
              </div>
            )}
            {followerTypes && followerTypes.length > 0 && donutSegments.length > 0 && (() => {
              const top = followerTypes.reduce((a, b) => (b.pctNum > a.pctNum ? b : a));
              const hoveredType = qualityHoveredIdx != null ? followerTypes[qualityHoveredIdx] : null;
              return (
                <div className="flex flex-col gap-5 rounded-xl border border-border p-6">
                  <div className="text-body-sm-semibold uppercase text-[#171717]">
                    {audienceNoun}s Quality
                  </div>
                  <div className="flex flex-col items-center gap-4">
                    <div className="relative" style={{ width: 190, height: 190 }}>
                      <Donut
                        segments={donutSegments}
                        size={190}
                        thickness={24}
                        activeIndex={qualityHoveredIdx}
                        onHoverChange={setQualityHoveredIdx}
                      />
                      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-heading-h4/9 font-medium text-[#171717]">
                          {Math.round((hoveredType ?? top).pctNum)}%
                        </span>
                        <span className="text-body-sm-regular text-[#737373]">{(hoveredType ?? top).label}</span>
                      </div>
                    </div>
                    <div className="flex w-full flex-wrap gap-x-3 gap-y-2.5">
                      {followerTypes.map((t, i) => (
                        <div
                          key={t.label}
                          onMouseEnter={() => setQualityHoveredIdx(i)}
                          onMouseLeave={() => setQualityHoveredIdx(null)}
                          className={`flex items-center gap-2 rounded-md px-1 py-0.5 -mx-1 transition-colors ${
                            qualityHoveredIdx === i ? 'bg-muted' : ''
                          }`}
                        >
                          <span className="size-2.5 shrink-0 rounded-full" style={{ background: t.color }} />
                          <span className="text-caption-medium text-[#171717]">{t.label}</span>
                          <span className="text-caption-regular text-[#737373]">{t.pct}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          );
        })()}

        {/* Row 2 — TOP DEMOGRAPHIC (Country/State/City toggle) | TOP LANGUAGES
            (Figma node 2019:69314). Country/State/City share one card with a
            segmented pill; Languages sits beside it. */}
        {(demoDims.length > 0 || (activeAudience.languages?.length ?? 0) > 0) && (
          <div className="mt-5 grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
            {demoDims.length > 0 && activeDemo && (
              <div className="flex flex-col gap-4 rounded-xl border border-border p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="text-body-sm-semibold uppercase text-[#171717]">Top Demographic</div>
                  {demoDims.length > 1 && (
                    <div className="flex items-center gap-1.5 rounded-full border border-border bg-muted p-1">
                      {demoDims.map((d) => (
                        <button
                          key={d.key}
                          type="button"
                          onClick={() => setDemoTab(d.key)}
                          className={`flex h-6 items-center rounded-full px-3 text-caption-regular transition-colors ${
                            activeDemo.key === d.key
                              ? 'bg-card text-[#171717] shadow-sm'
                              : 'text-[#737373] hover:text-[#171717]'
                          }`}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-4">
                  {(() => {
                    const max = Math.max(...activeDemo.rows.map((r) => r.val), 1);
                    return activeDemo.rows.map((row, i) => (
                      <RankBarRow
                        key={row.label}
                        label={
                          activeDemo.key === 'country' && row.code ? (
                            <>
                              <CountryFlag code={row.code} />
                              {row.label}
                            </>
                          ) : (
                            row.label
                          )
                        }
                        value={`${row.val}%`}
                        pct={(row.val / max) * 100}
                        strong={i === 0}
                        strongColor="#6C09F7"
                        lightColor="#B89CF5"
                      />
                    ));
                  })()}
                </div>
              </div>
            )}
            {(activeAudience.languages?.length ?? 0) > 0 && (() => {
              const langs = (activeAudience.languages ?? []).slice(0, 5).map((l) => ({
                code: l.code,
                label: languageDisplayName(l.code),
                val: Math.round(l.value * 100) / 100,
              }));
              const maxL = Math.max(...langs.map((l) => l.val), 1);
              return (
                <div className="flex flex-col gap-4 rounded-xl border border-border p-6">
                  <div className="text-body-sm-semibold uppercase text-[#171717]">Top Languages</div>
                  <div className="flex flex-col gap-4">
                    {langs.map((l, i) => (
                      <RankBarRow
                        key={l.code}
                        label={l.label}
                        value={`${l.val}%`}
                        pct={(l.val / maxL) * 100}
                        strong={i === 0}
                        strongColor="#1BBFA6"
                        lightColor="#7FD4CB"
                      />
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Likers-only quick stats (extra detail) */}
        {audienceTab === 'likers' && (() => {
          const notFollowersPct = fmtDecimal2(iqLikers.likers_not_followers_percentage);
          const significantLikersPct = fmtDecimal2(iqLikers.significant_likers_percentage);
          if (notFollowersPct == null && significantLikersPct == null) return null;
          return (
            <div className="mt-5 grid grid-cols-2 gap-3 lg:max-w-md">
              {notFollowersPct != null && (
                <StatCard label="Likers Not Followers" value={`${notFollowersPct}%`} />
              )}
              {significantLikersPct != null && (
                <StatCard label="Significant Likers" value={`${significantLikersPct}%`} />
              )}
            </div>
          );
        })()}

        {/* Follower/subscriber quality + credibility (extra detail, below
            countries/languages). YouTube has only significant % (Phyllo omits
            follower_types and credibility_score for YT). */}
        {SHOW_FOLLOWER_QUALITY && (followerTypes || credibility != null || significantPct != null) && (
          <div className="mt-5">
            <div className="mb-3 text-body-sm-semibold uppercase tracking-wider text-muted-foreground">{audienceNoun} Quality</div>
            <div className="flex flex-wrap items-start gap-4">
              {followerTypes && donutSegments.length > 0 && (
                <div className="flex items-start gap-3">
                  <Donut
                    segments={donutSegments}
                    activeIndex={qualityHoveredIdx2}
                    onHoverChange={setQualityHoveredIdx2}
                  />
                  <div className="space-y-1.5">
                    {followerTypes.map((t, i) => (
                      <div
                        key={t.label}
                        onMouseEnter={() => setQualityHoveredIdx2(i)}
                        onMouseLeave={() => setQualityHoveredIdx2(null)}
                        className={`flex items-center gap-2 rounded-md px-1 py-0.5 -mx-1 text-caption-regular transition-colors ${
                          qualityHoveredIdx2 === i ? 'bg-muted' : ''
                        }`}
                      >
                        <div className="size-2 rounded-full shrink-0" style={{ background: t.color }} />
                        <span className="font-semibold text-foreground">{t.label}</span>
                        <span className="text-muted-foreground">{t.pct}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {credibility != null && (
                <div className="flex flex-col items-center rounded-xl border border-border bg-muted px-4 py-3 min-w-[80px]">
                  <div className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">Credibility</div>
                  <div className="mt-0.5 text-heading-h5 text-foreground">{credibility}%</div>
                  <Badge label={credibility >= 70 ? 'Good' : credibility >= 50 ? 'Fair' : 'Poor'} type={credibility >= 70 ? 'good' : credibility >= 50 ? 'fair' : 'poor'} />
                </div>
              )}
              {fmtDecimal2(significantPct) != null && (
                <div className="flex flex-col items-center rounded-xl border border-border bg-muted px-4 py-3 min-w-[80px]">
                  <div className="flex items-center gap-0.5 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <span className="text-center">Significant {audienceNoun}s</span>
                    <InfoTip label={`What does Significant ${audienceNoun}s mean?`}>
                      How many of this creator&apos;s {audienceNoun.toLowerCase()}s are
                      notable accounts in their own right — bigger or influential profiles
                      rather than everyday or throwaway ones.
                    </InfoTip>
                  </div>
                  <div className="mt-0.5 text-heading-h5 text-foreground">{fmtDecimal2(significantPct)}%</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Follower Reachability + Audience Interests — each its own
            full-width card (same rounded-xl/border/p-6 chrome as the
            Audience Fit card above), stacked one per row. Reachability only
            shows for the followers tab (not likers). */}
        {(() => {
          const reachabilityRows = audienceTab !== 'likers' ? reachabilityData : null;
          const showReachability = !!reachabilityRows && reachabilityRows.length > 0;
          if (!showReachability && !interests) return null;
          return (
            <div className="mt-5 flex flex-col gap-5">
              {showReachability && reachabilityRows && (
                <div className="rounded-xl border border-border p-6">
                  <div className="mb-1 text-body-sm-semibold uppercase text-[#171717]">Follower Reachability</div>
                  <div className="mb-4 text-caption-regular text-muted-foreground">% of followers by number of accounts they follow</div>
                  <FollowerReachabilityChart data={reachabilityRows} />
                </div>
              )}
              {interests && (
                <div className="rounded-xl border border-border p-6">
                  <div className="mb-1 text-body-sm-semibold uppercase text-[#171717]">Audience Interests</div>
                  <div className="mb-4 text-caption-regular text-muted-foreground">Top interest categories this audience follows</div>
                  <InterestChips items={[...interests.col1, ...interests.col2]} />
                </div>
              )}
            </div>
          );
        })()}

        {/* AI-insight banner (Figma figd_1 bottom): intentionally NOT rendered.
            No audience-insight string exists anywhere in the InsightIQ payload
            (InsightIQAudience/Profile have no such field), so there is nothing
            real to wire — and we never fabricate copy. Add a guarded render here
            if/when the BE supplies an audience-insight field. */}
        </>)}
        {audienceTab === 'commenters' && iqProfile.audience_commenters && (
          <AudienceCommentersTab commenters={iqProfile.audience_commenters} />
        )}
      </div>
      )}

      {/* Brand Affinity — a standalone top-level card, not nested inside
          Audience Demographics: Figma's "Audience Demographics" frame
          (node 2019:69221) has no Brand Affinity child at all, it's a
          sibling section (node 2019:69441). Independent of the
          followers/likers/commenters tab above (uses iqAudience/iqProfile
          directly). Text-only chips: the Figma reference shows brand logos,
          but we intentionally render names only (no logo assets). */}
      {/* Admin-only — same isAdmin gate as PricingSection/CreatorGmvSection
          below. Brands don't see Brand Affinity. */}
      {isAdmin && brandAffinity && (
        <div className="rounded-2xl border border-border bg-card p-5 mb-4">
          <div className="mb-1 flex items-center gap-1 text-heading-h6 text-[#171717]">
            Brand Affinity
            <InfoTip label="What is Brand Affinity?">
              Brands this creator&apos;s audience tends to follow and engage with — a
              read on which brands already feel natural to their followers and where
              there&apos;s a built-in fit for a partnership.
            </InfoTip>
          </div>
          <div className="mb-4 text-body-sm-regular text-[#737373]">Brands aligned with this audience</div>
          <div className="flex flex-wrap gap-2">
            {brandAffinity.map((brand) => (
              <span key={brand} className="rounded-full border border-border bg-muted px-3 py-0.5 text-[12px] font-semibold text-foreground">
                {brand}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Reputation History + Engagement Histogram + Content (Mid tier) ─ */}
      <CollapsibleSection
        title="Reputation History"
        present={(iqProfile.reputation_history?.length ?? 0) >= 2}
      >
        <ReputationHistorySection profile={iqProfile} />
      </CollapsibleSection>

      <CollapsibleSection
        title="Engagement Rate Histogram"
        present={(iqProfile.engagement_rate_histogram?.length ?? 0) > 0}
      >
        <EngagementHistogramSection profile={iqProfile} />
      </CollapsibleSection>

      {/* Top Mentions / Top Hashtags (Figma 2019-68968) */}
      <PillMoreSection title="Top Mentions" items={iqProfile.top_mentions} prefix="@" />
      <PillMoreSection title="Top Hashtags" items={iqProfile.top_hashtags} prefix="#" />

      <CollapsibleSection
        title="Liker Audience Credibility"
        present={(iqProfile.audience_likes?.length ?? 0) > 0}
      >
        {iqProfile.audience_likes && (
          <LikerCredibilitySection bands={iqProfile.audience_likes} />
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title="Content"
        present={
          (iqProfile.recent_contents?.length ?? 0) > 0 ||
          (iqProfile.top_contents?.length ?? 0) > 0 ||
          (iqProfile.sponsored_contents?.length ?? 0) > 0
        }
      >
        <ContentTabsSection profile={iqProfile} />
      </CollapsibleSection>

      {/* ── Lower tier: Profile Relevant Tags + Mini-Profile Carousels ──── */}
      {isAdmin && (
        <CollapsibleSection
          title="Profile Relevant Tags"
          present={(iqProfile.profile_relevant_tags?.length ?? 0) > 0}
        >
          <ProfileRelevantTagsSection profile={iqProfile} />
        </CollapsibleSection>
      )}

      <CollapsibleSection
        title={`Notable ${audienceNoun}s`}
        present={(iqAudience.significant_followers?.length ?? 0) > 0}
      >
        <MiniProfileCarousel
          label={`Notable ${audienceNoun}s`}
          subtitle="Influential Accounts Following Them"
          profiles={iqAudience.significant_followers}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title="Notable Likers"
        present={(iqLikers.significant_likers?.length ?? 0) > 0}
      >
        <MiniProfileCarousel label="Notable Likers" profiles={iqLikers.significant_likers} />
      </CollapsibleSection>

      <CollapsibleSection
        title="Audience Lookalikes"
        present={(iqAudience.lookalikes?.length ?? 0) > 0}
      >
        <MiniProfileCarousel
          label="Audience Lookalikes"
          profiles={iqAudience.lookalikes}
        />
      </CollapsibleSection>

      {isAdmin && (
        <CollapsibleSection
          title="Similar Creators"
          present={(iqProfile.lookalikes?.length ?? 0) > 0}
        >
          <MiniProfileCarousel label="Similar Creators" profiles={iqProfile.lookalikes} />
        </CollapsibleSection>
      )}

      {/* ── Growth history ───────────────────────────────────────────────── */}
      {hasGrowthData && (
        <div className="rounded-2xl border border-border bg-card p-5 mb-4">
          <SectionTitle>
            {isInstagram ? 'Follower Growth' : 'Subscriber Growth'}
            <InfoTip label={isInstagram ? 'What is Follower Growth?' : 'What is Subscriber Growth?'}>
              This creator&apos;s total {isInstagram ? 'follower' : 'subscriber'} count at each
              month-end, so you can see whether the audience is growing or sliding. Hover a point
              for the exact number.
            </InfoTip>
          </SectionTitle>
          <ChartContainer config={{ subscribersRaw: { label: 'Subscribers', color: '#6C09F7' } }} className="h-48 w-full">
            <AreaChart data={reputationHistory} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f2" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#d4d4d4' }} />
              <YAxis
                domain={[growthDomainMin, 'dataMax']}
                tick={{ fontSize: 10, fill: '#d4d4d4' }}
                tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : String(Math.round(v))}
              />
              <Tooltip formatter={(v: unknown) => [fmtNum(Number(v)), isInstagram ? 'Followers' : 'Subscribers']} />
              <Area type="monotone" dataKey="subscribersRaw" stroke="#6C09F7" fill="#EDE9FE" strokeWidth={2} dot={false} />
            </AreaChart>
          </ChartContainer>
        </div>
      )}

      {/* ── Creator GMV (admin-only) ─────────────────────────────────────── */}
      {/* Sibling-of-profile on the wire — read from `doc`, not `iqProfile`.
          Reading from iqProfile was the original bug: silently always-undefined
          → present=false → section never rendered for any creator in prod. */}
      {isAdmin && (
        <CollapsibleSection
          title="Estimated Creator GMV"
          present={!!doc.creator_gmv?.price_range}
        >
          <CreatorGmvSection gmv={doc.creator_gmv} />
        </CollapsibleSection>
      )}

      {isAdmin && (
        <CollapsibleSection title="Pricing" present={!!doc.pricing}>
          {doc.pricing && <PricingSection pricing={doc.pricing} />}
        </CollapsibleSection>
      )}

      {isAdmin && (
        <CollapsibleSection title="Price Explanations" present={!!doc.price_explanations}>
          {doc.price_explanations && (
            <PriceExplanationsSection explanations={doc.price_explanations} />
          )}
        </CollapsibleSection>
      )}

      {/* ── Contact Details ──────────────────────────────────────────────── */}
      {(iqProfile.contact_details?.filter((c) => !['email', 'phone', 'telegram'].includes(String(c.type ?? '').toLowerCase())).length ?? 0) > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5 mb-4">
          <SectionTitle>Contact</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            {(iqProfile.contact_details ?? [])
              .filter((c) => !['email', 'phone', 'telegram'].includes(String(c.type ?? '').toLowerCase()))
              .map((c, i) => {
                // Platforms whose handles are conventionally written with @ —
                // instagram, twitter/x, tiktok, threads, snapchat, pinterest.
                // For these we render `@{label}` when label is present.
                // For everything else (youtube, facebook, linkedin, discord,
                // etc.) we render a generic 'Visit Profile →' button — YT
                // channel IDs and FB page IDs aren't user-friendly handles.
                const USERNAME_PLATFORMS = new Set(['instagram', 'twitter', 'x', 'tiktok', 'threads', 'snapchat', 'pinterest']);
                const platformType = String(c.type ?? '').toLowerCase();
                const labelTrimmed = c.label?.trim() ?? '';
                const showAsHandle = USERNAME_PLATFORMS.has(platformType) && labelTrimmed.length > 0;
                const display = showAsHandle ? `@${labelTrimmed}` : 'Visit Profile →';
                return (
                  <a
                    key={i}
                    href={c.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-xl bg-muted px-4 py-3 hover:bg-accent transition-colors"
                  >
                    <div className="mb-1.5 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">{c.type}</div>
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-body-sm-semibold text-foreground">{display}</span>
                      {c.verified === true && (
                        <span aria-label="verified" title="Verified by InsightIQ" className="shrink-0 text-emerald-600 text-[12px] font-semibold">✓</span>
                      )}
                    </div>
                  </a>
                );
              })}
          </div>
        </div>
      )}

      {/* ── Themes ──────────────────────────────────────────────────────── */}
      {themeRows.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5 mb-4">
          <SectionTitle>Content Themes</SectionTitle>
          <DetailedBarChart
            data={themeRows}
            maxVal={Math.max(...themeRows.map((t) => t.val), 1)}
            color={BLUE}
            height={80}
          />
        </div>
      )}
    </div>
  );
}
